import React, { useState, useEffect } from 'react';
import { api } from "../context/AuthContext";
import API_BASE_URL from "../Config/config";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
  InputAdornment,
  Grid,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { Search, FilterList } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

function RecruiterRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [feedback, setFeedback] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/super-admin/admin-requests');
      // Normalize API response to ensure it's always an array
      const responseData = response?.data;
      const safeData = Array.isArray(responseData)
          ? responseData
          : (Array.isArray(responseData?.data) ? responseData.data : []);
      setData(safeData);
    } catch (error) {
      console.error('Failed to fetch admin requests:', error);
      setData([]); // Fallback to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(req => {
    const matchesSearch = (req.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.company_specialization?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'All' ||
        (req.status || 'pending').toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const paginatedData = filteredData.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredData.length / 10);

  const handleAction = async (id, status) => {
    try {
      await api.patch(`/super-admin/admin-requests/${id}`, {
        status,
        adminFeedback: feedback,
      });
      // Refresh list
      const res = await api.get('/super-admin/admin-requests');
      const responseData = res?.data;
      const safeData = Array.isArray(responseData) 
        ? responseData 
        : (Array.isArray(responseData?.data) ? responseData.data : []);
      setData(safeData);
      setOpenDialog(false);
      setFeedback("");
    } catch (err) {
      console.error("Failed to update request:", err);
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      if (!url) {
        console.error('Download failed: URL is missing');
        return;
      }

      const response = await api.get(`/super-admin/admin-requests/download?url=${encodeURIComponent(url)}`, {
        responseType: 'blob'
      });
      
      // Check if the response is actually a JSON error hidden in a blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Server error');
      }

      // Determine filename with extension
      let finalFileName = fileName || 'document';
      if (!finalFileName.includes('.')) {
        // Try to extract extension from the URL (ignoring query params)
        const urlWithoutQuery = url.split('?')[0];
        const ext = urlWithoutQuery.split('.').pop();
        if (ext && ext.length <= 4 && /^[a-zA-Z0-9]+$/.test(ext)) {
          finalFileName = `${finalFileName}.${ext}`;
        }
      }

      // Create a blob from the response data
      // Use the content-type from headers if available, otherwise fallback to response.data.type
      const blobType = response.headers['content-type'] || response.data.type;
      const blob = new Blob([response.data], { type: blobType });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', finalFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const message = error.response?.data?.message || error.message || 'Failed to download document.';
      alert(`Error: ${message}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          {/*<Typography variant="h2" sx={{ color: '#1E3A8A', fontWeight: 'bold' }}>*/}
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
          >
            Recruiter Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and review requests from users who want to register as Hiring Managers.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by company name, specialization..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small">
            <Select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              displayEmpty
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                  <FilterList sx={{ fontSize: 20 }} />
                  Filters
                </Box>
              )}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E3A8A',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E40AF',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                Filters
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>CEO</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Specialization</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Submitted Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((req) => (
              <TableRow key={req.id} hover>
                <TableCell sx={{ fontWeight: 'medium', color: '#1E3A8A' }}>
                  {req.company_name}
                </TableCell>
                <TableCell>{req.company_ceo_name || 'N/A'}</TableCell>
                <TableCell>{req.company_specialization || 'N/A'}</TableCell>
                <TableCell>{req.contact_phone}</TableCell>
                <TableCell>
                  {new Date(req.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={(req.status || 'pending').toUpperCase()} 
                    color={
                      req.status === 'approved' ? 'success' : 
                      req.status === 'rejected' ? 'error' : 'warning'
                    }
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Button 
                    size="small" 
                    variant={req.status === 'pending' ? "contained" : "outlined"}
                    onClick={() => { setSelectedRequest(req); setOpenDialog(true); }}
                  >
                    {req.status === 'pending' ? "Review" : "View"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No requests found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedData.length > 0 ? (page - 1) * 10 + 1 : 0} to{" "}
          {Math.min(page * 10, filteredData.length)} of{" "}
          {filteredData.length} records
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          shape="rounded"
          color="primary"
          sx={{
            "& .MuiPaginationItem-root.Mui-selected": {
              backgroundColor: "#1E3A8A",
              "&:hover": {
                backgroundColor: "#1E40AF",
              },
            },
          }}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 'bold' }}>
          {selectedRequest?.company_name} - Request Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">CEO Name</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_ceo_name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.contact_phone}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Company Address</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_address}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Specialization</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_specialization || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_website || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Business Registration Number</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.business_registration_number || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Board Members</Typography>
                  <Typography variant="body1" gutterBottom>{selectedRequest.company_board_members || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Verification Documents</Typography>
                  {selectedRequest.documents && Array.isArray(selectedRequest.documents) && selectedRequest.documents.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                      {selectedRequest.documents.map((doc, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, border: '1px solid #e0e0e0', borderRadius: 0 }}>
                          <Typography variant="body2">
                            <strong>{doc.name}:</strong> View Document
                          </Typography>
                          <Box>
                            <Button 
                              size="small" 
                              component="a" 
                              href={doc.url?.startsWith('http') ? doc.url : `${API_BASE_URL}${doc.url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ mr: 1 }}
                            >
                              View
                            </Button>
                            <Button 
                              size="small" 
                              variant="outlined"
                              onClick={() => handleDownload(doc.url, doc.name)}
                            >
                              Download
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={(selectedRequest.status || 'pending').toUpperCase()} 
                    color={
                      selectedRequest.status === 'approved' ? 'success' : 
                      selectedRequest.status === 'rejected' ? 'error' : 'warning'
                    }
                    size="small"
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Feedback / Reason"
                sx={{ mt: 3 }}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                multiline
                rows={3}
                placeholder="Provide feedback for the recruiter..."
                defaultValue={selectedRequest.admin_feedback || ""}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit">Close</Button>
          {selectedRequest?.status === 'pending' && (
            <>
              <Button color="error" variant="outlined" onClick={() => handleAction(selectedRequest.id, 'rejected')}>Reject Request</Button>
              <Button color="success" variant="contained" onClick={() => handleAction(selectedRequest.id, 'approved')}>Approve Request</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RecruiterRequests;
